import { getDefinition, type DefLookup } from "./catalog";
import { buildDcCircuitTopology, dcInputPorts, type DcSourcePair } from "./dc-circuit-topology";
import { splitV, v, type Graph } from "./diagnostics/graph";
import { collectLoadCircuits } from "./load-circuits";
import { indexNodesById } from "./node-tree";
import { nominalClass } from "./voltage-domain";
import type { ComponentDefinition, ComponentNode, System } from "./types";

/** Dometic CRX50/65 installation manual 4445103148 (2021-06-01), pp24–26,
 * figures 14/15 and §6.6 step 3. The 12 V chart specifies 4/6/10 mm² through 8 m;
 * fuse advice is 15 A at 12 V and 7.5 A at 24 V. Larger fuses are NOT established
 * as universally prohibited across CRX revisions. The 24 V graphic is left
 * for manual verification instead of inventing a formula from its artwork.
 * https://media.dometic.com/externalassets/dometic-coolmatic-crx-50-e_9600028416_82511.pdf */
export type FridgeCablePolicy = {
  minimumWireMm2?: number;
  manufacturerInstallationUnverified?: true;
  /** Only on the uninterrupted dedicated positive lead, never a shared feed. */
  fuseA?: number;
  fuseUnverified?: true;
};
export type FridgeInstallation = {
  nodeId: string;
  label: string;
  cableIds: string[];
  positiveCableIds: string[];
  dedicatedPositiveCableIds: string[];
  minimumWireMm2?: number;
  fuseA?: number;
  routeLengthM?: number;
  verificationReason?: "identity" | "route" | "voltage" | "24v-chart" | "length";
};
export type FridgeInstallationAnalysis = {
  installations: FridgeInstallation[];
  byCable: Map<string, FridgeCablePolicy>;
};
const profiles = new Map([
  ["load-fridge-dometic", "CRX-50"],
  ["load-fridge-dometic-crx-65", "CRX-65"],
]);

/** A copied id or modified custom definition is not model identity evidence. */
export function isSupportedCrx(def: ComponentDefinition, node: ComponentNode): boolean {
  const model = profiles.get(node.definitionId);
  if (!model || node.customDefinition || def.id !== node.definitionId ||
      def.category !== "load" || def.manufacturer !== "Dometic" || def.model !== model) return false;
  const canonical = getDefinition(node.definitionId)!;
  const electrical = (value: ComponentDefinition) => JSON.stringify([
    value.ampDraw, value.ampSurge, value.watts, value.voltage, value.compatibleVoltages,
    value.ampOutput, value.outputVoltage, value.internalDcConnections, value.oneWayDcConnections,
    value.simulateStates, value.settings,
    value.terminals.map(t => [t.id, t.polarity, t.direction, t.maxAmp]),
  ]);
  return electrical(def) === electrical(canonical);
}

/** Walk one dedicated lead through passive internal ties. Branches are counted
 * by physical cables leaving the tied posts, not unused busbar stud count. */
function dedicatedLead(start: string, graph: Graph, system: System, lookup: DefLookup): string[] {
  const nodes = indexNodesById(system.nodes);
  const first = (graph.adj.get(start) ?? []).filter(e => e.via.kind === "cable");
  if (first.length !== 1) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  let step = first[0];
  for (;;) {
    if (step.via.kind !== "cable" || seen.has(step.via.cableId)) break;
    seen.add(step.via.cableId);
    out.push(step.via.cableId);
    const at = nodes[splitV(step.vertex).nodeId];
    const def = at?.kind === "component" ? lookup(at.definitionId, at.customDefinition) : undefined;
    if (!def || !["distribution", "monitor"].includes(def.category)) break;
    const tied = new Set([step.vertex]), queue = [step.vertex];
    for (let i = 0; i < queue.length; i++) for (const edge of graph.adj.get(queue[i]) ?? []) {
      if (edge.via.kind !== "intra" || tied.has(edge.vertex)) continue;
      tied.add(edge.vertex); queue.push(edge.vertex);
    }
    const next = [...tied].flatMap(at => graph.adj.get(at) ?? [])
      .filter(e => e.via.kind === "cable" && !seen.has(e.via.cableId));
    if (next.length !== 1) break;
    step = next[0];
  }
  return out;
}

const cache = new WeakMap<System, WeakMap<DefLookup, FridgeInstallationAnalysis>>();
export function computeFridgeInstallationPolicies(system: System, lookup: DefLookup = getDefinition): FridgeInstallationAnalysis {
  let byLookup = cache.get(system);
  if (!byLookup) { byLookup = new WeakMap(); cache.set(system, byLookup); }
  const cached = byLookup.get(lookup);
  if (cached) return cached;
  const installations: FridgeInstallation[] = [], byCable = new Map<string, FridgeCablePolicy>();
  const nodes = indexNodesById(system.nodes);
  const supported = system.nodes.filter((node): node is ComponentNode => node.kind === "component" &&
    (profiles.has(node.definitionId) || (node.definitionId === "custom" && profiles.has(node.customDefinition?.id ?? ""))));
  if (!supported.length) { const empty = { installations, byCable }; byLookup.set(lookup, empty); return empty; }
  const topology = buildDcCircuitTopology(system, lookup);
  const circuits = collectLoadCircuits(system, lookup);
  const ports = dcInputPorts(system, lookup);
  const cables = new Map(system.cables.map(c => [c.id, c]));
  const sourceVoltage = (source: DcSourcePair): number | undefined => {
    if (source.kind === "battery") {
      // Source-range discovery has a legacy 12 V fallback. It is not evidence
      // for a manufacturer installation voltage when a battery omitted volts.
      if (!source.batteryNodeIds.every(id => {
        const n = nodes[id];
        const voltage = n?.kind === "component" ? lookup(n.definitionId, n.customDefinition)?.voltage : undefined;
        return voltage != null && Number.isFinite(voltage) && voltage > 0;
      })) return undefined;
      // A mixed-voltage parallel group does not establish the highest member's
      // voltage. Series levels may differ, but each parallel level must agree.
      if (source.levels.some(level => new Set(level.map(id => {
        const n = nodes[id] as ComponentNode;
        return nominalClass(lookup(n.definitionId, n.customDefinition)!.voltage!);
      })).size !== 1)) return undefined;
      return source.nominalV;
    }
    const n = source.nodeId ? nodes[source.nodeId] : undefined;
    const def = n?.kind === "component" ? lookup(n.definitionId, n.customDefinition) : undefined;
    return def?.outputVoltage;
  };
  for (const node of supported) {
    const definition = lookup(node.definitionId, node.customDefinition);
    const identityKnown = !!definition && isSupportedCrx(definition, node);
    const label = definition?.model ?? definition?.name ?? profiles.get(node.definitionId)!;
    const nodePorts = ports.filter(p => p.nodeId === node.id);
    const port = nodePorts[0];
    const resolution = port ? topology.resolvePort(port) : undefined;
    const matching = circuits.filter(c => splitV(c.loadVertex).nodeId === node.id);
    const complete = matching.filter(c => c.complete);
    const sourceIds = new Set(matching.map(c => c.sourceId));
    const sources = topology.sources.filter(s => sourceIds.has(s.id));
    const volts = sources.map(sourceVoltage);
    const classes = new Set(volts.map(value => value != null && ((value >= 10 && value <= 15) || (value >= 20 && value <= 30)) ? nominalClass(value) : undefined));
    const voltage = classes.size === 1 && !classes.has(undefined) ? [...classes][0] : undefined;
    const fuseA = !identityKnown ? undefined : voltage === 12 ? 15 : voltage === 24 ? 7.5 : undefined;
    const dedicatedPositiveCableIds = [...new Set((nodePorts.length ? nodePorts.flatMap(p => p.positive) : [v(node.id, "pos")])
      .flatMap(start => dedicatedLead(start, topology.graph, system, lookup)))];
    const dedicatedNegativeCableIds = [...new Set((nodePorts.length ? nodePorts.flatMap(p => p.negative) : [v(node.id, "neg")])
      .flatMap(start => dedicatedLead(start, topology.graph, system, lookup)))];
    const positiveCableIds = [...new Set(matching.flatMap(c => c.pos))];
    const cableIds = [...new Set([...matching.flatMap(c => [...c.pos, ...c.neg]), ...dedicatedPositiveCableIds, ...dedicatedNegativeCableIds])];
    // Multiple source choices/parallel copper and incomplete route extraction
    // need physical verification; one convenient witness cannot certify length.
    const routeKnown = complete.length === 1 && !resolution?.unsupported.length && !resolution?.incomplete.length;
    const route = complete[0];
    const lengthOf = (ids: string[]) => ids.reduce((sum, id) => sum + (cables.get(id)?.lengthM ?? NaN), 0);
    const validLengths = route && [...route.pos, ...route.neg].every(id => {
      const length = cables.get(id)?.lengthM;
      return length != null && Number.isFinite(length) && length > 0;
    });
    // The manual plots one-way length. Use the longer complete leg, including
    // shared feeders and split leads, rather than halving an asymmetric loop.
    const routeLengthM = route && validLengths ? Math.max(lengthOf(route.pos), lengthOf(route.neg)) : undefined;
    const verificationReason = !identityKnown ? "identity" : !routeKnown || routeLengthM == null ? "route"
      : voltage == null ? "voltage" : voltage === 24 ? "24v-chart"
      : routeLengthM > 8 ? "length" : undefined;
    const minimumWireMm2 = !verificationReason && routeLengthM != null
      ? routeLengthM >= 6 ? 10 : routeLengthM >= 4 ? 6 : 4 : undefined;
    installations.push({ nodeId: node.id, label, cableIds, positiveCableIds, dedicatedPositiveCableIds,
      minimumWireMm2, fuseA, routeLengthM, verificationReason });
    for (const id of cableIds) {
      const policy = byCable.get(id) ?? {};
      if (minimumWireMm2 != null) policy.minimumWireMm2 = Math.max(policy.minimumWireMm2 ?? 0, minimumWireMm2);
      if (verificationReason) policy.manufacturerInstallationUnverified = true;
      byCable.set(id, policy);
    }
    for (const id of dedicatedPositiveCableIds) {
      const policy = byCable.get(id) ?? {};
      if (fuseA != null) policy.fuseA = Math.max(policy.fuseA ?? 0, fuseA);
      else policy.fuseUnverified = true;
      byCable.set(id, policy);
    }
  }
  const result = { installations, byCable };
  byLookup.set(lookup, result);
  return result;
}
